import React, { useEffect, useRef, useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { Client, IMessage } from '@stomp/stompjs';
import { useLocation } from 'react-router';
import { getAccessToken, isLoggedIn } from 'common/utils/auth';
import { getUserInfo } from 'common/utils/secureStorage';
import { getGatewayUrl } from 'common/axios/getApiUrl';
import { ChatRepository } from 'repository/chatRepository';
import { BlockRepository } from 'repository/blockRepository';
import { chatBannerStackAtom, ChatNotificationItem, MAX_VISIBLE_BANNERS } from './chatNotificationStore';

interface ServerChatMessage {
    messageId?: string;
    roomId: string;
    senderId: string;
    senderNickname?: string;
    senderProfileUrl?: string;
    content: string;
    messageType: 'TEXT' | 'IMAGE' | 'SYSTEM' | 'LOCATION';
    createdAt: string;
}

interface ChatRoomLite {
    roomId: string;
    roomName?: string;
}

/**
 * 전역 채팅 알림 배너 Provider.
 *
 * - 로그인 시 STOMP 연결을 열고 사용자가 속한 모든 방을 구독.
 * - 새 메시지 도착 시: 본인 메시지가 아니고 그 방의 채팅 화면을 보고 있지 않으면 배너 push.
 * - 로그인/로그아웃 라이프사이클에 반응 (storage event + atom).
 * - STOMP 재연결 시 토큰을 매번 fresh 하게 읽어 보호.
 */
export const ChatNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const pushBanner = useSetAtom(chatBannerStackAtom);
    const stompRef = useRef<Client | null>(null);
    const subscribedRoomsRef = useRef<Set<string>>(new Set());
    const myUserNoRef = useRef<string>('');
    const roomNameMapRef = useRef<Map<string, string>>(new Map());
    const seenMessageIdsRef = useRef<Set<string>>(new Set());
    const pathRef = useRef<string>(location.pathname);
    // 내가 차단한 사용자 — STOMP 메시지가 들어와도 banner 로 표시 안 하기 위함.
    // 차단/해제 시 'dokocrew:blockChange' / 'dokocrew:unblockChange' 이벤트로 갱신.
    const blockedSendersRef = useRef<Set<string>>(new Set());

    // 인증 상태 추적용 ref — setup 트리거에서 마지막으로 본 토큰과 비교해 변화 시 재구성.
    const lastTokenRef = useRef<string | null>(null);

    // 현재 경로를 ref 로 보관 — STOMP 콜백에서 stale closure 없이 최신 경로 참조.
    useEffect(() => {
        pathRef.current = location.pathname;
    }, [location.pathname]);

    const enqueueBanner = useCallback((item: Omit<ChatNotificationItem, 'id' | 'visible'>) => {
        // 중복 방지: messageId 기준
        const dedupKey = item.messageId || `${item.roomId}-${item.createdAt}-${item.senderNickname}`;
        if (seenMessageIdsRef.current.has(dedupKey)) return;
        seenMessageIdsRef.current.add(dedupKey);
        // seen set 크기 제한 (메모리 폭주 방지)
        if (seenMessageIdsRef.current.size > 500) {
            const arr = Array.from(seenMessageIdsRef.current);
            seenMessageIdsRef.current = new Set(arr.slice(-300));
        }

        const banner: ChatNotificationItem = {
            ...item,
            id: dedupKey,
            visible: true,
        };
        pushBanner((prev) => {
            // 동일 messageId 가 이미 있으면 무시
            if (prev.some((b) => b.id === banner.id)) return prev;
            const next = [banner, ...prev];
            return next.slice(0, MAX_VISIBLE_BANNERS);
        });
    }, [pushBanner]);

    const handleIncomingMessage = useCallback((raw: ServerChatMessage, roomName?: string) => {
        if (!raw) return;
        // SYSTEM 메시지는 배너로 띄우지 않음
        if (raw.messageType === 'SYSTEM') return;
        // 본인 메시지는 무시
        if (String(raw.senderId) === myUserNoRef.current) return;
        // 차단한 사용자 메시지는 배너로 띄우지 않음 (push 와 일관)
        if (blockedSendersRef.current.has(String(raw.senderId))) return;
        // "해당 채팅방" 만 suppress — 다른 방의 메시지는 채팅 리스트/방에서도 배너로 표시.
        // includes 만 쓰면 roomId prefix-매칭 오탐 가능 (예: ROOM-AAA 가 ROOM-AAABBB 로 포함됨).
        const roomPath = `/chat/room/${raw.roomId}`;
        const inThisRoom = pathRef.current === roomPath
            || pathRef.current.startsWith(`${roomPath}/`)
            || pathRef.current.startsWith(`${roomPath}?`)
            || pathRef.current.startsWith(`${roomPath}#`);
        if (inThisRoom) return;

        enqueueBanner({
            messageId: raw.messageId,
            roomId: raw.roomId,
            roomName: roomName || roomNameMapRef.current.get(raw.roomId),
            senderNickname: raw.senderNickname || '알 수 없음',
            senderProfileUrl: raw.senderProfileUrl,
            content: raw.content || '',
            messageType: raw.messageType,
            createdAt: raw.createdAt,
        });
    }, [enqueueBanner]);

    const teardown = useCallback(() => {
        try {
            stompRef.current?.deactivate();
        } catch { /* ignore */ }
        stompRef.current = null;
        subscribedRoomsRef.current.clear();
        roomNameMapRef.current.clear();
        myUserNoRef.current = '';
        blockedSendersRef.current.clear();
    }, []);

    const loadBlockedSenders = useCallback(async () => {
        try {
            const res: any = await BlockRepository.onGetMyBlocks();
            const list: Array<{ blockedUserNo: number }> =
                res?.list ?? res?.data?.list ?? [];
            blockedSendersRef.current = new Set(list.map((b) => String(b.blockedUserNo)));
        } catch (e) {
            // 실패해도 치명적 X — 빈 set 유지하고 다음 사이클에 재시도
            console.warn('[chat-notif] 차단 목록 로드 실패', e);
        }
    }, []);

    const setupStomp = useCallback(async () => {
        // 이미 활성 client 가 있으면 무시 — 중복 setup 방지
        if (stompRef.current?.active) return;
        if (!isLoggedIn()) return;
        const info = getUserInfo();
        myUserNoRef.current = info?.userNo ? String(info.userNo) : '';

        // 0. 차단 사용자 목록 사전 로딩 (병렬 fetch 와 함께 처리)
        loadBlockedSenders();

        // 1. 채팅방 목록 fetch
        let rooms: ChatRoomLite[] = [];
        try {
            const res: any = await ChatRepository.onGetChatRoomList();
            const list = res?.list ?? res?.data?.list ?? [];
            rooms = Array.isArray(list) ? list : [];
        } catch (e) {
            console.warn('[chat-notif] room list 로드 실패', e);
            return;
        }
        if (!rooms.length) return;

        rooms.forEach((r) => {
            if (r.roomId) roomNameMapRef.current.set(r.roomId, r.roomName || '');
        });

        // 2. STOMP 연결 — webSocketFactory 안에서 매 호출마다 토큰을 새로 읽어
        //    reconnect 사이클에 토큰 만료/갱신이 반영되게 한다.
        const wsUrl = `${getGatewayUrl()}/wizchat/ws-chat`.replace(/^http/, 'ws');
        const client = new Client({
            webSocketFactory: () => {
                const freshToken = getAccessToken();
                return new WebSocket(
                    wsUrl,
                    freshToken ? ['v12.stomp', `bearer.${freshToken}`] : ['v12.stomp'],
                );
            },
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            onConnect: () => {
                // 재연결 시 이전 subscription 은 무효화된 ws 세션에 묶여 있으므로
                // 이번 connect 사이클에서 다시 구독해야 함. ref 를 초기화 후 새로 채운다.
                subscribedRoomsRef.current.clear();
                rooms.forEach((r) => {
                    if (!r.roomId || subscribedRoomsRef.current.has(r.roomId)) return;
                    client.subscribe(`/topic/chat/${r.roomId}`, (message: IMessage) => {
                        try {
                            const parsed: ServerChatMessage = JSON.parse(message.body);
                            handleIncomingMessage(parsed, roomNameMapRef.current.get(r.roomId));
                        } catch (e) {
                            console.warn('[chat-notif] 메시지 파싱 실패', e);
                        }
                    });
                    subscribedRoomsRef.current.add(r.roomId);
                });
            },
            onStompError: (frame) => {
                console.warn('[chat-notif] STOMP 오류', frame.headers, frame.body);
            },
        });
        client.activate();
        stompRef.current = client;
    }, [handleIncomingMessage]);

    // navigation 마다 인증 상태 재평가 — 로그인/로그아웃 직후 사용자가 페이지 이동하면 자연스럽게 동기화.
    // (로그인 직후엔 보통 home/return-url 로 navigate, 로그아웃은 /login 으로 navigate)
    // 다른 탭의 토큰 변화는 storage event 로 동기화.
    useEffect(() => {
        const currentToken = isLoggedIn() ? getAccessToken() : null;
        if (currentToken !== lastTokenRef.current) {
            lastTokenRef.current = currentToken;
            if (currentToken) {
                setupStomp();
            } else {
                teardown();
            }
        }
    }, [location.pathname, setupStomp, teardown]);

    // 다른 탭에서 accessToken 변경 시 — 같은 사용자 다중 탭 동기화.
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key !== 'accessToken') return;
            const fresh = e.newValue;
            if (fresh !== lastTokenRef.current) {
                lastTokenRef.current = fresh;
                if (fresh) setupStomp();
                else teardown();
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [setupStomp, teardown]);

    // 같은 탭의 차단/해제 직후 banner 정책에 반영.
    // 다른 탭의 차단 변경은 localStorage 미사용이라 감지 불가 — 다음 마운트 사이클에 재로딩.
    useEffect(() => {
        const onBlockChange = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            const userNo = detail?.userNo != null ? String(detail.userNo) : null;
            if (userNo) blockedSendersRef.current.add(userNo);
            else loadBlockedSenders();  // userNo 정보 없으면 전체 다시 fetch
        };
        const onUnblockChange = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            const userNo = detail?.userNo != null ? String(detail.userNo) : null;
            if (userNo) blockedSendersRef.current.delete(userNo);
            else loadBlockedSenders();
        };
        window.addEventListener('dokocrew:blockChange', onBlockChange as EventListener);
        window.addEventListener('dokocrew:unblockChange', onUnblockChange as EventListener);
        return () => {
            window.removeEventListener('dokocrew:blockChange', onBlockChange as EventListener);
            window.removeEventListener('dokocrew:unblockChange', onUnblockChange as EventListener);
        };
    }, [loadBlockedSenders]);

    // 언마운트 시에만 teardown (라우터 root 가 사라질 때만 발생)
    useEffect(() => {
        return () => { teardown(); };
    }, [teardown]);

    return <>{children}</>;
};
